/**
 * Framework-free merge of recent searches and server-side suggestions into the
 * single ordered row list rendered by the dropdown.
 *
 * Shared by the React `SearchBar` and the embeddable widget so the two can
 * never drift apart.
 */

import type { SuggestionItem } from '../types/search';

export type DropdownRow =
  | { kind: 'history'; value: string }
  | { kind: 'suggestion'; value: SuggestionItem };

export const MIN_SUGGEST_LENGTH = 2;
export const HISTORY_MAX_DESKTOP = 5;
export const HISTORY_MAX_MOBILE = 3;
export const SUGGESTION_MAX = 5;

export interface MergeSuggestionRowsInput {
  query: string;
  history: string[];
  suggestions: SuggestionItem[];
  /** Cap on history rows shown while the query is below `minLength`. */
  historyMax?: number;
  /** Cap on history rows shown alongside server suggestions. */
  filteredHistoryMax?: number;
  minLength?: number;
}

/**
 * - Empty / sub-min query: recent history only.
 * - >= minLength chars: substring-filtered history + server suggestions, with
 *   suggestions whose text duplicates a shown history entry dropped.
 */
export function mergeSuggestionRows({
  query,
  history,
  suggestions,
  historyMax = HISTORY_MAX_DESKTOP,
  filteredHistoryMax = HISTORY_MAX_MOBILE,
  minLength = MIN_SUGGEST_LENGTH,
}: MergeSuggestionRowsInput): DropdownRow[] {
  const trimmed = query.trim();

  if (trimmed.length < minLength) {
    return history
      .slice(0, historyMax)
      .map((value) => ({ kind: 'history' as const, value }));
  }

  const lower = trimmed.toLowerCase();
  const filteredHistory = history
    .filter((h) => h.toLowerCase().includes(lower))
    .slice(0, filteredHistoryMax);

  const historyTextSet = new Set(filteredHistory.map((h) => h.toLowerCase()));
  const filteredSuggestions = suggestions.filter(
    (s) => !historyTextSet.has(s.text.toLowerCase())
  );

  return [
    ...filteredHistory.map((value) => ({ kind: 'history' as const, value })),
    ...filteredSuggestions.map((value) => ({ kind: 'suggestion' as const, value })),
  ];
}

/** Text carried by a row, regardless of its kind. */
export function rowText(row: DropdownRow): string {
  return row.kind === 'history' ? row.value : row.value.text;
}
